import dayjs from 'dayjs';
import { Formik } from 'formik';
import { prop } from 'ramda';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { assessmentManagerHomeRoute, assessmentManagerQuestionsRoute } from 'App/common/routeLookup';
import PopoverTooltip from 'App/components/Popovers/PopoverTooltip';
import env from 'App/components/config/config';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import useCompanySetting from 'App/hooks/useCompanySetting';
import AssessmentService from 'App/services/AssessmentService';
import { dateOnlySlashesFormat, toDate } from 'App/services/DateService';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import convertKeysToSnakeCase from 'App/utils/keyConversion/convertKeysToSnakeCase';
import CancelButton from 'Shared/form/CancelButton';
import { DatePickerField } from 'Shared/form/DatePickerField';
import SelectField from 'Shared/form/SelectField';
import SubmitButton from 'Shared/form/SubmitButton';
import { TextField } from 'Shared/form/TextField';
import { TextareaField } from 'Shared/form/TextareaField';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import { queryClient } from 'Shared/queryClient';
import toast from 'Shared/toast';
import useFetchAssessmentCategories from '../AssessmentManagerHome/hooks/useFetchAssessmentCategories';
import assessmentSettingsPropType from '../types/assessmentSettings';

const assessmentTypeOptions = [
    { value: 0, label: 'PCC' },
    { value: 1, label: 'Clinical' },
];

const AssessmentManagerSettingsForm = ({ existingSettings }) => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const { data: categories, isLoading: isCategoriesLoading } = useFetchAssessmentCategories();
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);
    const { data: ehrType } = useCompanySetting('ehrType', {
        defaultValue: '',
        convertToBoolean: false,
    });

    const initialValues = useMemo(() => {
        return {
            ...existingSettings,
            assessmentOwner: existingSettings?.assessmentOwner ?? true,
            description: existingSettings?.description || '',
            category: { id: existingSettings?.groupId || categoryId },
            retireDate: toDate(existingSettings?.retireDate),

            type: existingSettings
                ? assessmentTypeOptions.find((o) => o.value === existingSettings.assessmentType)
                : assessmentTypeOptions[0],
            needByDate: existingSettings?.needByDate === 'checked',
            therapyStartDate: existingSettings?.therapyStartDate === 'checked',

            therapySummaryNote: existingSettings?.summaryNote === 'checked',
            requireSummaryNote: existingSettings?.requireSummaryNote || false,
            ehrEncounterNote: existingSettings?.ehrEncounterNote || false,

            requireMedicationAssociation: existingSettings?.requireMedicationAssociation || false,
            generalSpecialty: existingSettings?.generalSpecialty || false,
            referralActivityForVista: existingSettings?.referralActivityForVista || false,

            mobileEnabled: existingSettings?.mobileEnabled || false,
            mobileAssessmentTextMessage: existingSettings?.mobileAssessmentTextMessage || '',
        };
    }, [categoryId, existingSettings]);

    const submitAssessment = async (values) => {
        if (existingSettings?.pollId) {
            return AssessmentService.updateAssessmentSettings(values.category.id, existingSettings.pollId, values);
        }
        return AssessmentService.createAssessmentSettings(values.category.id, values);
    };

    const goToAssessmentManagerHome = () => {
        navigate(generatePath(assessmentManagerHomeRoute, { categoryId }));
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                const payload = {
                    ...convertKeysToSnakeCase(values),
                    retire_date: dayjs(values.retireDate).format(dateOnlySlashesFormat),
                    type: values.type.value,
                    mobileEnabled: values.mobileEnabled,
                };
                const res = await submitAssessment(payload);
                toast.success(res.message);
                queryClient.invalidateQueries(['categoryId', values.category.id, 'assessmentId', res.poll_id]);
                return navigate(
                    generatePath(assessmentManagerQuestionsRoute, {
                        categoryId: values.category.id,
                        assessmentId: res.poll_id,
                    })
                );
            }}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <TextField
                                fieldName="name"
                                label="Name"
                                placeholder="Name"
                                required
                                requiredErrorText="Please enter a name"
                                maxLength={90}
                                disabled={!values.assessmentOwner}
                            />
                            <TextareaField
                                fieldName="description"
                                label={
                                    <>
                                        <span>Description</span>
                                        <span className="float-right">{values.description.length}/2500</span>
                                    </>
                                }
                                placeholder="Description"
                                required
                                requiredErrorText="Please enter a description"
                                maxLength={2500}
                                disabled={!values.assessmentOwner}
                            />

                            <SelectField
                                fieldName="category"
                                label="Therapeutic Category"
                                options={categories}
                                isLoading={isCategoriesLoading}
                                getOptionLabel={(o) => categories?.find((category) => category.id === o.id)?.name}
                                getOptionValue={prop('id')}
                                disabled={!isClinicalAdmin || !existingSettings}
                            />

                            {!isClinicalAdmin && values.retireDate && (
                                <div className="form-group">
                                    <strong>Retire Date: </strong>
                                    {dayjs(values.retireDate).format(dateOnlySlashesFormat)}
                                </div>
                            )}

                            {!isClinicalAdmin && <ToggleSwitchField fieldName="hide" label="Hide" />}
                            {isClinicalAdmin && !!existingSettings?.pollId && (
                                <>
                                    <ToggleSwitchField fieldName="retire" label="Retire" />
                                    {values.retire && (
                                        <DatePickerField
                                            fieldName="retireDate"
                                            label="Retire Date"
                                            required
                                        />
                                    )}
                                </>
                            )}
                        </Col>

                        <Col md={6}>
                            <SelectField
                                fieldName="type"
                                options={assessmentTypeOptions}
                                disabled={!values.assessmentOwner}
                                label="Assessment Type"
                            />

                            <ToggleSwitchField
                                fieldName="needByDate"
                                disabled={!values.assessmentOwner}
                                label={
                                    <>
                                        Need By Date{' '}
                                        <PopoverTooltip
                                            tooltipContent="Adds additional field to assessment. Recommended for initial and refill."
                                            tooltipId="need-by-date-popover"
                                        />
                                    </>
                                }
                            />
                            <ToggleSwitchField
                                fieldName="therapyStartDate"
                                disabled={!values.assessmentOwner}
                                label={
                                    <>
                                        Therapy Start Date{' '}
                                        <PopoverTooltip
                                            tooltipContent="Adds additional field to assessment. Recommended for initial and refill."
                                            tooltipId="therapy-start-date-popover"
                                        />
                                    </>
                                }
                            />

                            <ToggleSwitchField
                                fieldName="therapySummaryNote"
                                disabled={!values.assessmentOwner}
                                label="Summary Note"
                            />

                            {values.therapySummaryNote && (
                                <ToggleSwitchField
                                    fieldName="requireSummaryNote"
                                    label={
                                        <>
                                            Require Summary Note{' '}
                                            <PopoverTooltip
                                                tooltipContent="Updates the assessment to make the summary note field required."
                                                tooltipId="require-summary-note-popover"
                                            />
                                        </>
                                    }
                                />
                            )}
                            {values.therapySummaryNote && (
                                <ToggleSwitchField
                                    fieldName="enhancedSummaryNote"
                                    disabled={values.ehrEncounterNote}
                                    label={
                                        <>
                                            Enhanced Summary Note{' '}
                                            <PopoverTooltip
                                                tooltipContent="Displays 'Identified Clinical Issues' option with summary note."
                                                tooltipId="enhanced-summary-note-popover"
                                            />
                                        </>
                                    }
                                />
                            )}
                            {values.therapySummaryNote && ehrType && (
                                <ToggleSwitchField
                                    fieldName="ehrEncounterNote"
                                    label={
                                        <>
                                            EHR Encounter Note{' '}
                                            <PopoverTooltip
                                                tooltipContent="Creates an encounter note when an assessment is closed as complete."
                                                tooltipId="ehr-encounter-note-popover"
                                            />
                                        </>
                                    }
                                    onChange={(newValue) => {
                                        setFieldValue('ehrEncounterNote', newValue);
                                        if (newValue) {
                                            setFieldValue('enhancedSummaryNote', true);
                                            setFieldValue('requireMedicationAssociation', true);
                                        }
                                    }}
                                />
                            )}

                            <ToggleSwitchField
                                fieldName="requireMedicationAssociation"
                                disabled={values.ehrEncounterNote}
                                label={
                                    <>
                                        Require Medication Association{' '}
                                        <PopoverTooltip
                                            tooltipContent="Update Medication Association field as required in assessment."
                                            tooltipId="require-medication-assocation-popover"
                                        />
                                    </>
                                }
                            />

                            {isClinicalAdmin && values.category.id === env.therapyIds.general && (
                                <ToggleSwitchField
                                    fieldName="generalSpecialty"
                                    label={
                                        <>
                                            General Specialty{' '}
                                            <PopoverTooltip
                                                tooltipContent="Assigns the TC of “General” to be filtered as such in Work Queue."
                                                tooltipId="general-specialty-popover"
                                            />
                                        </>
                                    }
                                />
                            )}
                            {isClinicalAdmin && (
                                <ToggleSwitchField
                                    fieldName="referralActivityForVista"
                                    label={
                                        <>
                                            Referral Activity for Vista{' '}
                                            <PopoverTooltip
                                                tooltipContent="Used to identify & publish Vista Referral activities."
                                                tooltipId="referral-activity-for-vista-popover"
                                            />
                                        </>
                                    }
                                />
                            )}

                            {!isClinicalAdmin && (
                                <ToggleSwitchField
                                    fieldName="mobileEnabled"
                                    label={
                                        <>
                                            Mobile Enabled{' '}
                                            <PopoverTooltip
                                                tooltipContent="Assessments without a mobile question will not be sent to patients."
                                                tooltipId="mobile-enabled-popover"
                                            />
                                        </>
                                    }
                                />
                            )}
                            <TextareaField
                                fieldName="mobileAssessmentTextMessage"
                                maxLength={100}
                                disabled={!values.assessmentOwner}
                                label={
                                    <>
                                        Mobile Assessment Text Message{' '}
                                        <PopoverTooltip
                                            tooltipContent="Text messages are unsecure; do not include PHI."
                                            tooltipId="mobile-assessment-text-message-popover"
                                        />
                                        <span className="float-right">
                                            {values.mobileAssessmentTextMessage.length}/100
                                        </span>
                                    </>
                                }
                            />
                            <div>
                                [pharmacy name]: {values.mobileAssessmentTextMessage} Please complete the questionnaire
                                online at [mobile assessment url] or call [pharmacy phone] to speak with a pharmacy
                                representative. [Visit us at &#123;pharmacy url&#125;]. Text UNSUBSCRIBE to opt-out.
                            </div>
                        </Col>
                    </Row>

                    <FormButtonGroup>
                        <CancelButton
                            onClick={() => {
                                goToAssessmentManagerHome();
                            }}
                        />
                        <SubmitButton />
                    </FormButtonGroup>
                </form>
            )}
        </Formik>
    );
};

AssessmentManagerSettingsForm.propTypes = {
    existingSettings: assessmentSettingsPropType,
};
AssessmentManagerSettingsForm.defaultProps = {
    existingSettings: null,
};

export default AssessmentManagerSettingsForm;
